import React from 'react';
import styled from 'styled-components/macro';
import { Field } from 'formik';
import { CategoryType } from './ReportCategories';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import { ReactComponent as Add } from 'assets/icons/plus.svg';

const Table = styled.table`
  border-collapse: collapse;
  margin-bottom: 2rem;
  width: 100%;

  Td,
  tr,
  Th {
    border: 1px solid black;
    text-align: center;
    position: relative;
  }
`;
const Td = styled.td<{ width?: string }>`
  padding: 2px 4px !important;
  position: relative;
  width: ${({ width }) => width || 'auto'};
`;

const Th = styled.th<{ width?: string }>`
  padding: 2px 4px !important;
  width: ${({ width }) => width || 'auto'};
`;

const GroupTitle = styled.h5`
  font-weight: 700;
  font-size: 0.875rem;
  margin: 0.5rem 0;
  text-transform: capitalize;
`;

const Input = styled.input`
  border: none;
  outline none;
  padding: 5px 0;
  border-bottom: 1px solid black;
`;

const Error = styled.span`
  position: absolute;
  color: red;
  top: 0;
  left: 50%;
  transform: translate(-50%, 0);
`;

interface Props {
  values: CategoryType[];
  push: (cat: CategoryType) => void;
  remove: (index: number) => void;
  name: string;
  errors: any;
  touched: any;
}

const CategoryTable = ({
  name,
  values,
  push,
  remove,
  errors,
  touched,
}: Props) => {
  return (
    <>
      <GroupTitle>{name}</GroupTitle>
      <Table>
        <thead>
          <tr>
            <Th width="200px" rowSpan={2}>
              Name
            </Th>
            <Th style={{ textAlign: 'center' }} colSpan={4}>
              Translation
            </Th>
            <Th
              rowSpan={2}
              width="40px"
              style={{ cursor: 'pointer' }}
              onClick={() =>
                push({
                  name: '',
                  i18n: {
                    en: '',
                    de: '',
                    fr: '',
                    it: '',
                  },
                })
              }
            >
              <Add />
            </Th>
          </tr>
          <tr>
            <Th>EN</Th>
            <Th>DE</Th>
            <Th>FR</Th>
            <Th>IT</Th>
          </tr>
        </thead>
        <tbody>
          {values.map((category: CategoryType, index: number) => {
            const rowErrors = errors && errors[index];
            const rowTouched = touched && touched[index];

            const isAnyError = rowErrors && rowTouched;
            const isTranslationError =
              isAnyError && rowErrors.i18n && rowTouched.i18n;

            const isNameError = isAnyError && rowErrors.name && rowTouched.name;

            const isEnglishTranslationError =
              isTranslationError && rowErrors.i18n.en && rowTouched.i18n.en;

            const isDeutshTranslationError =
              isTranslationError && rowErrors.i18n.de && rowTouched.i18n.de;

            const isFrancisTranslationError =
              isTranslationError && rowErrors.i18n.fr && rowTouched.i18n.fr;

            const isItalishTranslationError =
              isTranslationError && rowErrors.i18n.it && rowTouched.i18n.it;

            return (
              <tr key={index}>
                <Td width="200px">
                  <Field name={`${name}[${index}].name`} as={Input} />
                  {isNameError && <Error>{rowErrors.name}</Error>}
                </Td>
                <Td>
                  <Field name={`${name}[${index}].i18n.en`} as={Input} />
                  {isEnglishTranslationError && (
                    <Error>{rowErrors.i18n.en}</Error>
                  )}
                </Td>
                <Td>
                  <Field name={`${name}[${index}].i18n.de`} as={Input} />
                  {isDeutshTranslationError && (
                    <Error>{rowErrors.i18n.de}</Error>
                  )}
                </Td>
                <Td>
                  <Field name={`${name}[${index}].i18n.fr`} as={Input} />
                  {isFrancisTranslationError && (
                    <Error>{rowErrors.i18n.fr}</Error>
                  )}
                </Td>
                <Td>
                  <Field name={`${name}[${index}].i18n.it`} as={Input} />
                  {isItalishTranslationError && (
                    <Error>{rowErrors.i18n.it}</Error>
                  )}
                </Td>
                <Td
                  width="40px"
                  style={{ cursor: 'pointer' }}
                  onClick={() => remove(index)}
                >
                  <Trash title="remove" />
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

export default CategoryTable;
